import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VendorCard = ({ 
  vendor,
  onMessage,
  onViewProfile,
  onToggleFavorite,
  variant = 'default' // 'default', 'compact', 'featured'
}) => {
  const [isFavorited, setIsFavorited] = useState(vendor.isFavorited || false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    onToggleFavorite?.(vendor.id, !isFavorited);
  };

  const handleMessage = (e) => {
    e.stopPropagation();
    onMessage?.(vendor.id);
  };

  const handleViewProfile = () => {
    onViewProfile?.(vendor.id);
  };

  const renderRating = () => {
    const stars = [];
    const fullStars = Math.floor(vendor.rating);
    const hasHalfStar = vendor.rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={12} color="#DAA520" className="fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="StarHalf" size={12} color="#DAA520" className="fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(vendor.rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={12} color="#E8E2DD" />
      );
    }

    return stars;
  };

  if (variant === 'compact') {
    return (
      <div className="bg-white rounded-lg shadow-soft border border-border hover:shadow-medium transition-smooth cursor-pointer group">
        <div className="p-4">
          <div className="flex items-center space-x-3">
            <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-surface-100 flex-shrink-0">
              <Image
                src={vendor.image}
                alt={vendor.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onLoad={() => setImageLoaded(true)}
              />
              {vendor.isVerified && (
                <div className="absolute top-1 right-1 w-4 h-4 bg-success rounded-full flex items-center justify-center">
                  <Icon name="Check" size={10} color="#FFFFFF" />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-text-primary truncate">
                {vendor.name}
              </h3>
              <p className="text-sm text-text-secondary font-caption truncate">
                {vendor.category}
              </p>
              <div className="flex items-center space-x-2 mt-1">
                <div className="flex items-center space-x-1">
                  {renderRating()}
                </div>
                <span className="text-xs text-text-muted font-data">
                  ({vendor.reviewCount})
                </span>
              </div>
            </div>

            <button
              onClick={handleToggleFavorite}
              className={`p-2 rounded-lg transition-smooth ${
                isFavorited 
                  ? 'text-accent bg-accent-50 hover:bg-accent-100' :'text-text-muted hover:text-accent hover:bg-accent-50'
              }`}
            >
              <Icon name="Heart" size={16} color="currentColor" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'featured') {
    return (
      <div 
        onClick={handleViewProfile}
        className="bg-white rounded-xl shadow-medium border border-border hover:shadow-strong transition-smooth cursor-pointer group overflow-hidden"
      >
        <div className="relative h-64 bg-surface-100 overflow-hidden">
          <Image
            src={vendor.image}
            alt={vendor.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onLoad={() => setImageLoaded(true)}
          />
          
          {/* Overlay Badges */}
          <div className="absolute top-3 left-3 flex flex-col space-y-2">
            {vendor.isVerified && (
              <div className="bg-success text-success-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                <Icon name="Shield" size={12} color="currentColor" />
                <span>Verified</span>
              </div>
            )}
            {vendor.isTrending && (
              <div className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                <Icon name="TrendingUp" size={12} color="currentColor" />
                <span>Trending</span>
              </div>
            )}
          </div>

          <button
            onClick={handleToggleFavorite}
            className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-smooth ${
              isFavorited 
                ? 'bg-accent text-accent-foreground' 
                : 'bg-white/80 text-text-secondary hover:bg-white hover:text-accent'
            }`}
          >
            <Icon name="Heart" size={16} color="currentColor" />
          </button>

          {/* Quick Actions Overlay */}
          <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="flex items-center space-x-2">
              <Button
                variant="primary"
                size="sm"
                iconName="Eye"
                iconSize={14}
                className="flex-1 backdrop-blur-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewProfile();
                }}
              >
                View Profile
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="MessageCircle"
                iconSize={14}
                className="backdrop-blur-sm bg-white/90"
                onClick={handleMessage}
              >
                Message
              </Button>
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-1">
                {vendor.name}
              </h3>
              <p className="text-text-secondary font-caption">
                {vendor.category} • {vendor.culturalSpecialty}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4 mb-3">
            <div className="flex items-center space-x-1">
              {renderRating()}
              <span className="text-sm font-medium text-text-primary ml-1">
                {vendor.rating}
              </span>
              <span className="text-xs text-text-muted font-data">
                ({vendor.reviewCount} reviews)
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="MapPin" size={14} color="currentColor" />
              <span className="text-sm font-caption">{vendor.location}</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-text-primary">
                Starting at ${vendor.startingPrice?.toLocaleString()}
              </div>
              <div className="text-xs text-text-secondary font-caption">
                {vendor.priceRange}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div 
      onClick={handleViewProfile}
      className="bg-white rounded-lg shadow-soft border border-border hover:shadow-medium transition-smooth cursor-pointer group overflow-hidden"
    >
      <div className="relative h-48 bg-surface-100 overflow-hidden">
        <Image
          src={vendor.image}
          alt={vendor.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onLoad={() => setImageLoaded(true)}
        />
        
        {vendor.isVerified && (
          <div className="absolute top-2 left-2 bg-success text-success-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
            <Icon name="Shield" size={10} color="currentColor" />
            <span>Verified</span>
          </div>
        )}

        <button
          onClick={handleToggleFavorite}
          className={`absolute top-2 right-2 p-2 rounded-full backdrop-blur-sm transition-smooth ${
            isFavorited 
              ? 'bg-accent text-accent-foreground' 
              : 'bg-white/80 text-text-secondary hover:bg-white hover:text-accent'
          }`}
        >
          <Icon name="Heart" size={14} color="currentColor" />
        </button>
      </div>

      <div className="p-4">
        <h3 className="font-heading font-semibold text-text-primary mb-1 truncate">
          {vendor.name}
        </h3>
        <p className="text-sm text-text-secondary font-caption mb-2 truncate">
          {vendor.category}
        </p>

        <div className="flex items-center space-x-1 mb-2">
          {renderRating()}
          <span className="text-sm font-medium text-text-primary ml-1">
            {vendor.rating}
          </span>
          <span className="text-xs text-text-muted font-data">
            ({vendor.reviewCount})
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-1 text-text-secondary">
            <Icon name="MapPin" size={12} color="currentColor" />
            <span className="font-caption truncate">{vendor.location}</span>
          </div>
          <div className="text-primary font-medium">
            ${vendor.startingPrice?.toLocaleString()}+
          </div>
        </div>

        <div className="mt-3 flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="MessageCircle"
            iconSize={14}
            fullWidth
            onClick={handleMessage}
          >
            Message
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VendorCard;